import { type FC, useMemo, useState } from "react";
import { Check, Copy, Edit, MoreHorizontal, Trash2 } from "lucide-react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/_atoms/alert-dialog";
import { Button } from "@/components/_atoms/button";
import { DropdownMenuItem } from "@/components/_atoms/dropdown-menu";
import { Popover, PopoverTrigger } from "@/components/_atoms/popover";
import { cn } from "@/lib/cn";

import { useCreateStoredView } from "../../hooks/api/use-create-stored-view";
import { useDeleteStoredView } from "../../hooks/api/use-delete-stored-view";
import { useUpdateActiveView } from "../../hooks/api/use-update-active-view";
import { useCurrentViewContext } from "../../hooks/use-current-view-context";
import { filterDatatableStateByColumns, getCleanedDatatableState } from "../../models/datatable-state-model";
import { StoredView } from "../../models/stored-view-model";
import { useDatatableContext } from "../datatable/use-datatable-context";

interface StoredViewListMenuItemProps {
  storedView: StoredView;
  isActive: boolean;
  user: {
    id: string;
  };
  onEdit: (storedView: StoredView) => void;
  onSelect?: () => void;
  dropdownOpen: string | null;
  setDropdownOpen: (value: string | null) => void;
}

export const StoredViewListMenuItem: FC<StoredViewListMenuItemProps> = ({
  storedView,
  isActive,
  user,
  onEdit,
  onSelect,
  dropdownOpen,
  setDropdownOpen,
}) => {
  const { t } = useTranslation("common");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { createStoredView, loading: createStoredViewLoading } = useCreateStoredView();
  const { deleteStoredView, loading: deleteStoredViewLoading } = useDeleteStoredView();
  const { updateActiveView, loading: updateActiveViewLoading } = useUpdateActiveView();

  const { setState: setDatatableState, columns } = useDatatableContext();
  const { currentActiveView } = useCurrentViewContext();

  const validColumnIdSet = useMemo(() => new Set(columns.map((c) => c.columnId)), [columns]);

  const storedViewDatatableState = useMemo(() => {
    const cleanedDatatableState = getCleanedDatatableState(storedView);
    if (cleanedDatatableState) {
      return filterDatatableStateByColumns(cleanedDatatableState, validColumnIdSet);
    }

    return null;
  }, [storedView, validColumnIdSet]);

  const handleDeleteView = async (storedView: StoredView) => {
    if (!storedView.id) {
      return;
    }

    if (deleteStoredViewLoading) {
      return;
    }

    deleteStoredView(
      { id: storedView.id },
      {
        onSuccess: () => {
          if (currentActiveView) {
            updateActiveView({
              id: currentActiveView.id,
              storedViewId: null,
            });
          }

          toast.success(t("view.deleteSuccess"), { description: t("view.deleteSuccessDescription", { name: storedView.name }) });
        },
        onError: () => {
          toast.error(t("errors.title"), { description: t("view.deleteError") });
        },
      },
    );
  };

  const handleDuplicate = async () => {
    if (createStoredViewLoading) {
      return;
    }

    if (!storedViewDatatableState) {
      toast.error(t("errors.title"), { description: t("view.duplicateError") });
      return;
    }

    createStoredView(
      {
        name: `${storedView.name} ${t("view.copySuffix")}`,
        key: storedView.key,
        ownerId: user.id,
        ownerType: "USER",
        workspaceId: storedView.workspaceId,
        default: false,
        columnFilters: storedViewDatatableState.columnFilters,
        columnOrder: storedViewDatatableState.columnOrder,
        columnVisibility: storedViewDatatableState.columnVisibility,
        sorting: storedViewDatatableState.sorting,
        showColumnHeaders: storedViewDatatableState.showColumnHeaders,
        globalFilter: storedViewDatatableState.globalFilter,
        expanded: storedViewDatatableState.expanded,
        sectionColumnId: storedViewDatatableState.sectionColumnId,
        subSectionColumnId: storedViewDatatableState.subSectionColumnId,
      },
      {
        onSuccess: (newView) => {
          toast.success(t("view.duplicateSuccess"), { description: t("view.duplicateSuccessDescription", { name: newView.name }) });
        },
        onError: () => {
          toast.error(t("errors.title"), { description: t("view.duplicateError") });
        },
      },
    );

    setDropdownOpen(null);
  };

  const handleSelect = async () => {
    if (updateActiveViewLoading) {
      return;
    }

    if (!storedViewDatatableState) {
      toast.error(t("errors.title"), { description: t("view.selectError") });
      return;
    }

    onSelect?.();

    if (currentActiveView) {
      updateActiveView({
        id: currentActiveView.id,
        storedViewId: storedView.id,
      });

      setDatatableState({ state: storedViewDatatableState, skipApiPropagation: true });
      return;
    }

    setDatatableState({ state: storedViewDatatableState });
  };

  return (
    <>
      <DropdownMenuItem
        key={storedView.id}
        className={cn(
          "group flex cursor-default items-center justify-between rounded-xs px-2 py-1.5 text-xs",
          isActive && "bg-accent",
        )}
        onSelect={() => handleSelect()}
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate">{storedView.name}</span>
          {storedView.default && (
            <span className="shrink-0 rounded-sm bg-muted px-1 py-0.5 text-[10px] leading-none text-muted-foreground">
              {t("view.baseView")}
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <Popover
            open={dropdownOpen === storedView.id}
            onOpenChange={(open) => {
              if (!open) {
                setDropdownOpen(null);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                aria-label={t("a11y.openActions")}
                className={cn(
                  "h-5 w-5 rounded-sm p-0 transition-opacity hover:bg-accent",
                  dropdownOpen === storedView.id
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100",
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDropdownOpen(storedView.id);
                }}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                className="shadow-md z-100 w-[160px] rounded-md border bg-popover p-1"
                sideOffset={5}
                side="right"
                align="start"
                onInteractOutside={(e) => {
                  e.preventDefault();
                }}
              >
                <Button
                  variant="ghost"
                  size="xs"
                  className="flex w-full items-center justify-start gap-2 rounded-xs px-2 py-1.5 text-xs hover:bg-accent"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDropdownOpen(null);
                    onEdit(storedView);
                  }}
                >
                  <Edit className="h-3.5 w-3.5 text-muted-foreground" />
                  {t("view.editView")}
                </Button>
                <Button
                  variant="ghost"
                  size="xs"
                  className="flex w-full items-center justify-start gap-2 rounded-xs px-2 py-1.5 text-xs hover:bg-accent"
                  onClickCapture={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDuplicate();
                  }}
                >
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  {t("view.duplicate")}
                </Button>
                <div className="my-1 h-px bg-border" />
                <Button
                  variant="ghost"
                  size="xs"
                  className="flex w-full items-center justify-start gap-2 rounded-xs px-2 py-1.5 text-xs text-destructive hover:bg-accent hover:text-destructive"
                  onClickCapture={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDropdownOpen(null);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("view.delete")}
                </Button>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </Popover>
          {isActive && <Check className="h-3.5 w-3.5 text-foreground/60" />}
        </div>
      </DropdownMenuItem>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("view.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("view.deleteConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("view.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDeleteView(storedView)}
            >
              {t("view.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
